import { Spin, Empty } from "antd"; // Add Empty to import
import dayjs from "dayjs";
import {
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Package,
  Settings,
  Trash2,
  User,
} from "lucide-react";
import React from "react";
import { useNotification } from "~/hooks";
import { Priority, TaskStatus } from "~/utils/constants";
import {
  useGetMostUrgentTasks,
  useDeleteTask,
  useUpdateTask,
  TASK_KEYS,
} from "~/services/task";
import { useTaskStore } from "../../dispatch/task/list-tasks/task.store";
import { useQueryClient } from "@tanstack/react-query";

export const UrgentTasks: React.FC = () => {
  const { openTaskModal } = useTaskStore();
  const { data: tasks, isLoading: isLoadingTask } = useGetMostUrgentTasks();
  const qc = useQueryClient();
  const { success, error } = useNotification();
  const mutation = useUpdateTask();
  const mutationDelete = useDeleteTask();

  // Complete task
  const completeTask = (taskId: string) => {
    const formData = new FormData();
    formData.append("taskStatus", TaskStatus.COMPLETED);
    mutation.mutate(
      { taskId, payload: formData },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: TASK_KEYS.GET_MOST_URGENT_TASKS(),
          });
          success({
            message: "Task finished!",
          });
        },
        onError: (err) => {
          error({
            message: "Can not finish task",
            description: err.message,
          });
        },
      }
    );
  };

  // Delete task
  const deleteTask = (taskId: string) => {
    mutationDelete.mutate(taskId, {
      onSuccess: () => {
        qc.invalidateQueries({
          queryKey: TASK_KEYS.GET_MOST_URGENT_TASKS(),
        });
        success({
          message: "Delete task successfully!",
        });
      },
      onError: (err) => {
        error({
          message: "Can not delete task!",
          description: err.message,
        });
      },
    });
  };

  // Get status badge
  const getStatusBadge = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.COMPLETED:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
            Completed
          </span>
        );
      case TaskStatus.IN_PROGRESS:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
            In Progress
          </span>
        );
      case TaskStatus.PENDING:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  // Get priority indicator
  const getPriorityIndicator = (priority: Priority) => {
    switch (priority) {
      case Priority.HIGH:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
            High
          </span>
        );
      case Priority.MEDIUM:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
            Medium
          </span>
        );
      case Priority.LOW:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
            Low
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
            {priority}
          </span>
        );
    }
  };

  // Get task type icon
  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case "maintenance":
        return <Settings size={16} />;
      case "inventory":
        return <Package size={16} />;
      case "meeting":
        return <User size={16} />;
      case "artwork":
        return <Package size={16} />;
      default:
        return <Package size={16} />;
    }
  };

  return (
    <div className="pb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Urgent Tasks</h3>
        <button className="text-sm text-blue-600 hover:text-blue-800">
          View All
        </button>
      </div>
      <Spin
        spinning={
          isLoadingTask || mutation.isPending || mutationDelete.isPending
        }
      >
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {tasks?.length ? (
            tasks.map((task) => (
              <div
                key={task.id}
                className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <div
                        className={`p-1 rounded ${task.taskStatus === TaskStatus.COMPLETED
                          ? "bg-green-100"
                          : task.priority === Priority.HIGH
                            ? "bg-red-100"
                            : "bg-blue-100"
                          }`}
                      >
                        {getTaskTypeIcon(task.taskType)}
                      </div>
                      <div className="font-medium text-sm truncate">
                        {task.title}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mb-2 truncate line-clamp-2">
                      {task.description}
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar size={12} />
                        <span>{dayjs(task.dueDate).format("YYYY-MM-DD")}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={12} />
                        <span>{task.startTime || "N/A"}</span>
                      </div>
                      {task.assignees && task.assignees.length > 0 && (
                        <div className="flex items-center space-x-1 truncate">
                          <User size={12} />
                          <span className="truncate">
                            {task.assignees.length > 2
                              ? `${task.assignees[0].user?.username} +${task.assignees.length - 1
                              }`
                              : task.assignees
                                .map((a) => a.user?.username)
                                .filter(Boolean)
                                .join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1 ml-2">
                    <div className="flex flex-col items-end space-y-1">
                      {getStatusBadge(task.taskStatus)}
                      {getPriorityIndicator(task.priority)}
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => openTaskModal(task.id)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                      >
                        <Edit size={14} />
                      </button>
                      {task.taskStatus !== TaskStatus.COMPLETED && (
                        <button
                          onClick={() => completeTask(task.id)}
                          className="p-1 text-gray-400 hover:text-green-600"
                        >
                          <CheckCircle size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center h-40 mb-4">
              <Empty
                description={
                  <span className="text-gray-500 text-base font-medium">
                    No data
                  </span>
                }
              />
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};
